import type { LearningAttempt } from '../models/learning.models';

export interface SkillEvidenceObservation {
  readonly questionId: string;
  readonly correct: boolean;
  readonly observedAt: string;
}

export interface SkillEvidenceSummary {
  readonly profileId: string;
  readonly skillId: string;
  readonly attempts: number;
  readonly correct: number;
  readonly incorrect: number;
  readonly observations: readonly SkillEvidenceObservation[];
}

/**
 * Describes what Learning Garden has locally observed for one profile/skill.
 *
 * This deliberately does not infer mastery, level, readiness or a next activity.
 * Those are routing decisions and require a separately versioned product policy;
 * institutional curriculum/proficiency sources do not define app-level thresholds.
 */
export function buildSkillEvidence(
  attempts: readonly LearningAttempt[],
  profileId: string,
  skillId: string
): SkillEvidenceSummary {
  const relevant = attempts
    .filter((attempt) => attempt.profileId === profileId && attempt.skillId === skillId)
    .sort((left, right) => {
      const byTime = left.createdAt.localeCompare(right.createdAt);
      return byTime || left.id.localeCompare(right.id);
    });

  const observations = relevant.map((attempt): SkillEvidenceObservation => ({
    questionId: attempt.questionId,
    correct: attempt.correct,
    observedAt: attempt.createdAt
  }));
  const correct = observations.filter((observation) => observation.correct).length;

  return {
    profileId,
    skillId,
    attempts: observations.length,
    correct,
    incorrect: observations.length - correct,
    observations
  };
}
